import { useState } from 'react';
import { DrawerSection } from '../../billing/Drawer';
import { useRemoveTeamMember, useTeamMembers } from '../../../hooks/useTeams';
import { useToast } from '../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../utils/apiError';
import type { Role } from '../../../services/roles';
import { AddTeamMemberForm } from './AddTeamMemberForm';
import { TeamMemberRow } from './TeamMemberRow';

interface TeamMembersSectionProps {
    team: Role;
    /** Gates the add/remove controls; the roster itself is always visible. */
    canManage: boolean;
}

export function TeamMembersSection({ team, canManage }: TeamMembersSectionProps) {
    const { members, isLoading } = useTeamMembers(team.id);
    const { mutateAsync: removeMember, isPending: isRemoving } = useRemoveTeamMember();
    const { showToast } = useToast();
    const [showAddMember, setShowAddMember] = useState(false);
    const [removingUserId, setRemovingUserId] = useState<string | null>(null);

    const handleRemove = async (userId: string) => {
        setRemovingUserId(userId);
        try {
            await removeMember({ teamId: team.id, userId });
            showToast('Member removed', 'success');
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to remove member'), 'error');
        } finally {
            setRemovingUserId(null);
        }
    };

    return (
        <DrawerSection
            title="Members"
            action={
                <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">
                        {members.length} {members.length === 1 ? 'member' : 'members'}
                    </span>
                    {canManage && !showAddMember && (
                        <button
                            type="button"
                            onClick={() => setShowAddMember(true)}
                            className="shrink-0 bg-white text-gray-800 border border-gray-300 text-xs font-semibold px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            + Add member
                        </button>
                    )}
                </div>
            }
        >
            <div className="flex flex-col gap-3">
                {canManage && showAddMember && (
                    <AddTeamMemberForm
                        team={team}
                        existingMemberIds={members.map((member) => member.id)}
                        onCancel={() => setShowAddMember(false)}
                    />
                )}

                {isLoading ? (
                    <p className="text-sm text-gray-400">Loading members…</p>
                ) : members.length === 0 ? (
                    !showAddMember && <p className="text-sm text-gray-400">No members yet.</p>
                ) : (
                    <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                        {members.map((member) => (
                            <TeamMemberRow
                                key={member.id}
                                member={member}
                                onRemove={
                                    canManage
                                        ? () => {
                                              void handleRemove(member.id);
                                          }
                                        : undefined
                                }
                                isRemoving={isRemoving && removingUserId === member.id}
                            />
                        ))}
                    </div>
                )}
            </div>
        </DrawerSection>
    );
}
